import {
  Component,
  h,
  State,
  ComponentInterface,
  Event,
  EventEmitter,
  Host,
  Prop,
} from "@stencil/core";
import { Calendar, CalendarEntry, IThemeOptions } from "../../../models";
import { Schedule } from "@auvious/utils";
import {
  AppConfigService,
  AppConfigService as Config,
  DateTimeFactory,
  FormHelper,
  ScheduleService,
  ThemeService,
  TranslateService,
} from "../../../services";
import {
  ISCheduleContactBase,
  IScheduleRequest,
  IScheduledAppointmentTimeFrame,
} from "../../../models/schedule/ISchedule";
import moment from "moment";
import { NotificationService } from "../../../services/notification.service";
import { Subscription } from "rxjs";
enum step {
  collection,
  verify,
}

interface IState {
  step?: step;
  startAt?: moment.Moment;
  timeslotAt?: moment.Moment;
  timeslots?: moment.Moment[];
  isLoading?: boolean;
  isCreatingTimeslots?: boolean;
}

const DEFAULT_STATE: IState = {
  step: step.collection,
  startAt: null,
  timeslotAt: null,
  timeslots: [],
  isLoading: false,
  isCreatingTimeslots: false,
};

@Component({
  tag: "app-schedule-timeslots",
  styleUrl: "app-schedule-timeslots.scss",
  shadow: true,
})
export class AppScheduleTimeslots implements ComponentInterface {
  @Prop() scheduledAt: Date;
  @Prop({ mutable: true }) contact: ISCheduleContactBase;
  @Prop() isDisclaimerAccepted: boolean;

  @Event({ bubbles: false }) ready: EventEmitter<IScheduleRequest>;

  @State() state: IState = { ...DEFAULT_STATE };
  @State() themeOptions: IThemeOptions;

  minDate: CalendarEntry;
  selectedDate: CalendarEntry;
  schedule: Schedule = null;
  timeslotIntervalMins = Config.scheduleIntervalMinutes;
  timeslotStartAt: moment.Moment;
  timeslotEndAt: moment.Moment;
  subscription = new Subscription();
  // customer: ISCheduleContactBase;

  connectedCallback(): void {
    this.subscription.add(
      ThemeService.optionsAvailable().subscribe(
        (options) => (this.themeOptions = options)
      )
    );
  }

  componentWillLoad(): void | Promise<void> {
    this.schedule = new Schedule(Config.schedule);
    this.minDate = Calendar.getToday();
    if (this.scheduledAt) {
      const startAt = moment(this.scheduledAt);
      this.selectedDate = Calendar.momentToCalendarEntry(startAt);
      this.setState({ startAt: startAt.clone(), timeslotAt: startAt.clone() });
    } else {
      const firstWorkingDay = this.getNextWorkingDay();
      this.selectedDate = Calendar.momentToCalendarEntry(firstWorkingDay);
      this.setState({ startAt: firstWorkingDay });
    }

    this.createTimeslots();
  }

  componentDidLoad(): void {}

  disconnectedCallback(): void {
    this.subscription.unsubscribe();
  }

  timeslotChanged(t: moment.Moment) {
    this.setState({ timeslotAt: t });
  }

  calendarDayChanged({ detail: entry }: CustomEvent<CalendarEntry>) {
    this.setState({
      timeslotAt: null,
      startAt: this.state.startAt
        .clone()
        .date(entry.day)
        .month(entry.month - 1)
        .year(entry.year),
    });
    this.createTimeslots();
  }

  customerChanged(event: CustomEvent<ISCheduleContactBase>) {
    this.contact = event.detail;
  }

  async next() {
    this.setState({ isLoading: true });

    const request: IScheduleRequest = {
      customer: this.contact,
      start: moment(this.state.startAt)
        .set("hour", this.state.timeslotAt.hour())
        .set("minute", this.state.timeslotAt.minute())
        .toDate(),
    };

    const isOverlap = await ScheduleService.isOverlappingSchedule(request);

    if (isOverlap) {
      NotificationService.warning("Please choose a different time", {
        body: "Another appointment has already been scheduled for that time.",
      });
      this.createTimeslots();
      this.setState({ isLoading: false, timeslotAt: null });
    } else {
      this.setState({ step: step.verify, isLoading: false });
    }
  }

  back() {
    this.selectedDate = Calendar.momentToCalendarEntry(this.state.startAt);
    this.setState({ step: step.collection });
  }

  async confirm() {
    if (FormHelper.isFormValid()) {
      this.setState({ isLoading: true });

      this.ready.emit({
        customer: this.contact,
        start: moment(this.state.startAt)
          .set("hour", this.state.timeslotAt.hour())
          .set("minute", this.state.timeslotAt.minute())
          .toDate(),
      });
    }
  }

  /**
   * Loop the days of the month until the first working day appears, starting from today
   * */
  private getNextWorkingDay(): moment.Moment {
    const minDateIndex = DateTimeFactory.create(
      ScheduleService.getProposedDateTime()
    );
    const lastDayOfMonth = DateTimeFactory.create()
      .date(1)
      .hours(0)
      .minutes(0)
      .seconds(0)
      .milliseconds(0)
      .add(1, "month")
      .subtract(1, "second");

    const openDays = this.schedule.getDays(
      minDateIndex.year(),
      minDateIndex.month()
    );
    let open = false;

    while (minDateIndex.isBefore(lastDayOfMonth, "hour") && !open) {
      // openDays is 0 based. moment.Moment starts at 1
      open = openDays[minDateIndex.date() - 1].open;
      if (!open) {
        minDateIndex.add(1, "day");
      }
    }
    return minDateIndex;
  }

  private async getScheduledAppointments(
    startAt: Date
  ): Promise<IScheduledAppointmentTimeFrame[]> {
    try {
      return await ScheduleService.getScheduledAppointmentsTimeframe(
        startAt,
        moment.duration(24, "hours")
      );
    } catch (ex) {
      return [];
    }
  }

  private async createTimeslots() {
    this.setState({ isCreatingTimeslots: true, timeslots: [] });

    const timeslots: moment.Moment[] = [];
    // start of today
    this.timeslotStartAt = this.state.startAt
      .clone()
      .hours(0)
      .minutes(0)
      .seconds(0)
      .milliseconds(0);
    // end of today
    this.timeslotEndAt = this.state.startAt
      .clone()
      .hours(23)
      .minutes(59)
      .seconds(59)
      .milliseconds(0);

    let timeslotIndex = this.timeslotStartAt.clone();

    const scheduled = await this.getScheduledAppointments(
      this.timeslotStartAt.toDate()
    );

    while (timeslotIndex.isBefore(this.timeslotEndAt)) {
      const openMinutes = this.schedule.getMinutes(
        timeslotIndex.year(),
        timeslotIndex.month(),
        timeslotIndex.date(),
        timeslotIndex.hours()
      );

      // calculate when does this timeslot end
      const timeslotEndsAt = timeslotIndex
        .clone()
        .add(this.timeslotIntervalMins, "minutes");

      // calculate if from now we add X timeslot duration minutes, what time is it?
      const timeslotNowEndsAt = moment().add(
        this.timeslotIntervalMins,
        "minutes"
      );

      // check if we have existing schedules at that time
      let isIncluded = true;
      if (scheduled.length > 0) {
        const slot = scheduled.find((s) => s.start.isSame(timeslotIndex));
        if (slot) {
          isIncluded = slot.count === 0;
        }
      }

      // we want the timeslot to end after (now + duration).
      // If the timeslot ends BEFORE (now + duration) it means there is not enough time to use the whole timeslot.
      if (
        isIncluded &&
        openMinutes[timeslotIndex.minute()].open &&
        timeslotEndsAt.isAfter(timeslotNowEndsAt)
      ) {
        timeslots.push(timeslotIndex.clone());
      }
      timeslotIndex.add(this.timeslotIntervalMins, "minute");
    }
    this.setState({ timeslots, isCreatingTimeslots: false });
  }

  private setState(state: IState) {
    this.state = { ...this.state, ...state };
  }

  private getTimeslotDateFormat(time: moment.Moment) {
    return time.format("hh:mm a");
  }

  private isTimeslotActive(t: moment.Moment) {
    return (
      !!this.state.timeslotAt &&
      t.hour() === this.state.timeslotAt.hour() &&
      t.minute() === this.state.timeslotAt.minute()
    );
  }

  // {false && (
  //   <div class="timeslot-progress">
  //     <div class="timeslot-progress-step">01/03 Schedule</div>
  //     <hr />
  //   </div>
  // )}

  render() {
    // const dateFormat = !!this.state.timeslotAt
    //   ? "dddd, MMMM DD yyyy, HH:mm"
    //   : "dddd, MMMM DD yyyy";
    const selectedDate = this.state.startAt.format("dddd, MMMM DD yyyy");
    const timeDifference = this.state.startAt.format("Z");
    const confirmText = TranslateService.instant("Confirm");
    const nextText = TranslateService.instant("Next");
    const backText = TranslateService.instant("Back");
    return (
      <Host>
        {this.state.step === step.collection && (
          <div>
            <av-card inline={true} open={true} class="calendar">
              <div slot="card-body">
                <av-calendar
                  minDate={this.minDate}
                  schedule={this.schedule}
                  selectedDate={this.selectedDate}
                  onDayChanged={this.calendarDayChanged.bind(this)}
                ></av-calendar>
              </div>
            </av-card>
            <p class="av-direction">
              <av-translate>Selected Date</av-translate>
              <div>
                <b>
                  <div>{selectedDate}</div>
                </b>
              </div>
            </p>
            <hr />
            {this.state.isCreatingTimeslots && (
              <div class="container-spinner">
                <av-spinner dark={!this.themeOptions.darkMode}></av-spinner>
              </div>
            )}
            {!this.state.isCreatingTimeslots &&
              this.state.timeslots.length === 0 && (
                <div class="container-empty">
                  <av-translate>
                    We are closed at this date. Please check the working hours.
                  </av-translate>
                </div>
              )}
            {this.state.timeslots.length > 0 && [
              <div class="timeslot-title av-direction">
                <span>
                  <av-translate>Pick a time slot</av-translate>
                </span>
                <small class="text-muted">{timeDifference}</small>
              </div>,
              <div class="timeslot-container">
                {this.state.timeslots.map((t) => {
                  return (
                    <button
                      class={{
                        timeslot: true,
                        active: this.isTimeslotActive(t),
                      }}
                      onClick={() => this.timeslotChanged(t)}
                    >
                      {this.getTimeslotDateFormat(t)}
                    </button>
                  );
                })}
              </div>,
            ]}
            <div class="actions actions-overflow">
              <div>
                {AppConfigService.scheduleDisclaimerURL && (
                  <av-disclaimer
                    url={AppConfigService.scheduleDisclaimerURL}
                    accepted={this.isDisclaimerAccepted}
                    onAcceptedChange={(e) =>
                      (this.isDisclaimerAccepted = e.detail)
                    }
                  ></av-disclaimer>
                )}
              </div>
              <av-button
                disabled={!this.state.timeslotAt || !this.isDisclaimerAccepted}
                color="accent"
                loading={this.state.isLoading}
                onClick={this.next.bind(this)}
              >
                {nextText}
              </av-button>
            </div>
          </div>
        )}
        {this.state.step === step.verify && (
          <div>
            <div class="av-direction">
              <av-translate>Please provide your contact details.</av-translate>
            </div>
            <app-schedule-contact
              contact={this.contact}
              onContactChanged={this.customerChanged.bind(this)}
            ></app-schedule-contact>

            <div class="actions">
              <av-button color="basic" onClick={this.back.bind(this)}>
                &larr; {backText}
              </av-button>
              <av-button
                data-tid="appointment/confirm"
                disabled={!this.state.timeslotAt}
                color="accent"
                loading={this.state.isLoading}
                onClick={this.confirm.bind(this)}
              >
                {confirmText}
              </av-button>
            </div>
          </div>
        )}
      </Host>
    );
  }
}
